import { EcleticaAPI } from "@/api/EcleticaAPI";
import { EcleticaTableConfiguration } from "@/core/EcleticaTableConfiguration";
import { Store } from "@/core/Store";
import { BaseStorage } from "@/storage/BaseStorage";
import Bugsnag from "@bugsnag/js";
import { CartItemBuilder } from "./CartItemBuilder";
import { OrderAPI } from "@/api/OrderAPI";

export class EcleticaTable {
  constructor() {
    this.ecleticaConfiguration = EcleticaTableConfiguration.fromStorage();

    Bugsnag.leaveBreadcrumb(
      "Configurações da Ecletica",
      this.ecleticaConfiguration.export()
    );

    const tableNumber = this.ecleticaConfiguration.externalId;
    this.storage = new BaseStorage({ key: `of-ecletica-table-${tableNumber}` });

    this.client = new EcleticaAPI({
      baseUrl: this.ecleticaConfiguration.apiUrl,
      apiToken: this.ecleticaConfiguration.apiToken,
      store: this.ecleticaConfiguration.store,
      network: this.ecleticaConfiguration.network,
      externalId: this.ecleticaConfiguration.externalId,
    });
  }

  getNewTransactionId() {
    const initRange = 999999;
    const timestampSeconds = Math.round(new Date().getTime() / 1000);
    const timestampMilliseconds = new Date().getTime() % 1000;
    return (
      initRange +
      Number(Store.id) +
      Number(this.ecleticaConfiguration.externalId) +
      timestampSeconds +
      timestampMilliseconds
    );
  }

  async addItems(cartItems = []) {
    const unavailableProductsResponse = await this.client.getUnavailableProducts();
    const unavailableProducts = unavailableProductsResponse.getData([]);

    if (unavailableProductsResponse.getErrors([]).length > 0) {
      throw new Error(`Não foi possível incluir os itens na mesa - ${unavailableProductsResponse.getErrors()[0]}`);
    }

    const unavailableProductSkus = unavailableProducts.map(
      (unavailableProduct) => unavailableProduct.cod_item
    );

    const transactionId = this.getNewTransactionId();
    const cartItemBuilder = new CartItemBuilder({
      transactionId,
      cartItems,
      unavailableProductSkus,
    });

    const convertedItems = cartItemBuilder.getItems();

    const addItensResponse = await this.client.addTableItems(
      this.ecleticaConfiguration.externalId,
      convertedItems,
      transactionId
    );

    if (addItensResponse.getErrors().length > 0) {
      throw new Error(`Não foi possível incluir os itens na mesa - ${addItensResponse.getErrors()[0]}`);
    }
  }

  async getTable() {
    const remoteTableResponse = await this.client.getTable(
      this.ecleticaConfiguration.externalId
    );

    if (remoteTableResponse.getErrors([]).length > 0) {
      throw new Error(remoteTableResponse.getErrors()[0]);
    }

    const remoteTable = remoteTableResponse.getData({});

    const tableStatusResponse = await this.client.getTableStatus(
      this.ecleticaConfiguration.externalId
    );
    if (tableStatusResponse.getErrors([]).length > 0) {
      throw new Error(`Não foi possível consultar a situação da mesa - ${tableStatusResponse.getErrors()[0]}`);
    }

    remoteTable.status = tableStatusResponse.getData(null);
    return remoteTable;
  }

  async getTableItems() {
    const itemsResponse = await this.client.getTableItems(
      this.ecleticaConfiguration.externalId
    );

    if (itemsResponse.getErrors([]).length > 0) {
      throw new Error(`Não foi possível consultar os itens da mesa - ${itemsResponse.getErrors()[0]}`);
    }

    const items = itemsResponse.getData();
    return items;
  }

  async getTableStatus() {
    const tableStatusResponse = await this.client.getTableStatus(
      this.ecleticaConfiguration.externalId
    );

    if (tableStatusResponse.getErrors([]).length > 0) {
      throw new Error(`Não foi possível consultar a situação da mesa - ${tableStatusResponse.getErrors()[0]}`);
    }

    const tableStatus = tableStatusResponse.getData();
    return tableStatus;
  }

  async callWaiter() {
    const callResponse = await this.client.callWaiter(this.ecleticaConfiguration.externalId);

    if (callResponse.getErrors([]).length > 0) {
      throw new Error(`Não foi possível chamar o garçom - ${callResponse.getErrors()[0]}`);
    }
  }

  async preClose() {
    const tableStatusResponse = await this.client.getTableStatus(
      this.ecleticaConfiguration.externalId
    );

    if (tableStatusResponse.getErrors([]).length > 0) {
      throw new Error(`Não foi possível consultar a situação da mesa - ${tableStatusResponse.getErrors()[0]}`);
    }

    const tableStatus = tableStatusResponse.getData(null);
    if (tableStatus === "Fechando") {
      throw new Error(`Mesa em fechamento`);
    }

    const transactionId = this.getNewTransactionId();

    const preCloseResponse = await this.client.preCloseTable(
      this.ecleticaConfiguration.externalId,
      transactionId
    );

    if (preCloseResponse.getErrors([]).length > 0) {
      throw new Error(`Não foi possível solicitar o fechamento da mesa - ${preCloseResponse.getErrors()[0]}`);
    }

    await OrderAPI.getTableTracking();

    return preCloseResponse.getData();
  }
}
